// اجزای مشترک صفحات PAT (AP-PAT-01): کلیدهای Query، تاریخ تولد شمسی متنی (ورود سریع با کیبورد)، نشان‌ها و قلاب‌های داده.
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import {
  unwrap,
  type PatientBadges as Badges,
  type ReferralSourceView,
  type TagDefinitionView,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { jalaliToBusinessDate, latinDigits, toJalali } from '@phoenix/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export const PATIENTS_PATH = '/patients';
export const PATIENT_SETTINGS_PATH = '/settings/patients';
// AP-PAT-02: صف تکراری‌ها (SCR-STAFF-104)، ویزارد ادغام (SCR-STAFF-105) و پرونده‌های ناقص (SCR-STAFF-107).
export const DUPLICATES_PATH = '/patients/duplicates';
export const MERGE_PATH = '/patients/merge';
export const INCOMPLETE_PATH = '/patients/incomplete';
// AP-PAT-03: صف درخواست‌های مراجعه‌کنندگان (SCR-STAFF-106) و لینک عمومی نسخه داده.
export const REQUESTS_PATH = '/patients/requests';

export function mergePath(survivorId: string, mergedId?: string): string {
  const query = new URLSearchParams({ survivor: survivorId });
  if (mergedId) {
    query.set('merged', mergedId);
  }
  return `${MERGE_PATH}?${query.toString()}`;
}

export const patKeys = {
  all: (tenantId: string | undefined) => ['patients', tenantId] as const,
  search: (tenantId: string | undefined, q: string) => ['patients', tenantId, 'search', q] as const,
  patient: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id] as const,
  duplicates: (tenantId: string | undefined, key: string) => ['patients', tenantId, 'duplicates', key] as const,
  sources: (tenantId: string | undefined, includeInactive: boolean) =>
    ['patients', tenantId, 'referral-sources', includeInactive] as const,
  fileNumber: (tenantId: string | undefined) => ['patients', tenantId, 'file-number'] as const,
  // AP-PAT-02 (همه زیر کلید پرونده تا ابطال پس از تغییر پرونده یکجا باشد).
  summary: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'summary'] as const,
  timeline: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'timeline'] as const,
  relations: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'relations'] as const,
  consents: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'consents'] as const,
  preferences: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'preferences'] as const,
  cards: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'cards'] as const,
  photo: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'photo'] as const,
  customFields: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'custom-fields'] as const,
  merges: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'merges'] as const,
  mergePreview: (tenantId: string | undefined, survivor: string, merged: string) =>
    ['patients', tenantId, 'merge-preview', survivor, merged] as const,
  tagDefinitions: (tenantId: string | undefined, includeInactive: boolean) =>
    ['patients', tenantId, 'tag-definitions', includeInactive] as const,
  consentTemplates: (tenantId: string | undefined) => ['patients', tenantId, 'consent-templates'] as const,
  recent: (tenantId: string | undefined) => ['patients', tenantId, 'recent'] as const,
  incomplete: (tenantId: string | undefined, overdueOnly: boolean) => ['patients', tenantId, 'incomplete', overdueOnly] as const,
  duplicateCandidates: (tenantId: string | undefined, status: string) => ['patients', tenantId, 'duplicate-candidates', status] as const,
  preferencesSettings: (tenantId: string | undefined) => ['patients', tenantId, 'settings', 'preferences'] as const,
  // AP-PAT-03.
  familyAccount: (tenantId: string | undefined, id: string) => ['patients', tenantId, 'patient', id, 'family-account'] as const,
  unmergeReview: (tenantId: string | undefined, id: string, mergeId: string) =>
    ['patients', tenantId, 'patient', id, 'merges', mergeId, 'review'] as const,
};

/** ذخیره Blob دانلود (بسته نسخه داده). */
export function saveBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** ابطال همه داده‌های یک پرونده (پروفایل، سربرگ، خط زمانی و بخش‌ها) پس از هر تغییر. */
export function usePatientInvalidation(id: string) {
  const { session } = useTenantSession();
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: patKeys.patient(session?.tenantId, id) });
    void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'search'] });
  };
}

/** سربرگ مشترک (US-PAT-081). */
export function usePatientSummary(id: string, enabled = true) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: patKeys.summary(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/summary', { params: { path: { id } } })),
    enabled: enabled && id.length > 0,
  });
}

/** برچسب‌های مدیریت‌شده (US-PAT-060). */
export function useTagDefinitions(includeInactive = false) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: patKeys.tagDefinitions(session?.tenantId, includeInactive),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/patients/tag-definitions', { params: { query: { includeInactive } } })),
    staleTime: 60_000,
    select: (data): TagDefinitionView[] => data.items,
  });
}

export function patientPath(id: string): string {
  return `${PATIENTS_PATH}/${id}`;
}

/**
 * تاریخ تولد شمسی متنی (FLOW-01: «۱۳__/__/__»): ارقام فارسی، عربی یا لاتین با / یا - یا . ؛ خروجی تاریخ کسب‌وکاری میلادی
 * yyyy-MM-dd برای API یا undefined (نامعتبر). سال دو رقمی پذیرفته نمی‌شود.
 */
export function parseJalaliInput(value: string | null | undefined): string | undefined {
  const text = latinDigits(value ?? '').trim();
  const match = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/.exec(text);
  if (!match) {
    return undefined;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1200 || year > 1500) {
    return undefined;
  }
  const business = jalaliToBusinessDate(year, month, day);
  const roundtrip = toJalali(business, { persianDigits: false });
  const expected = `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  return roundtrip === expected ? business : undefined;
}

/** نمایش تاریخ تولد در ورودی متنی (yyyy/MM/dd با ارقام فارسی). */
export function formatJalaliInput(value: string | null | undefined): string {
  return value ? toJalali(value) : '';
}

/** مقدار با تأخیر (جستجوی زنده). */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** منابع معرفی مستأجر (فعال برای انتخاب؛ همه برای تنظیمات). */
export function useReferralSources(includeInactive = false) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: patKeys.sources(session?.tenantId, includeInactive),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/patients/referral-sources', {
          params: { query: { includeInactive } },
        }),
      ),
    staleTime: 60_000,
    select: (data): ReferralSourceView[] => data.items,
  });
}

/** نشان‌های مراجعه‌کننده (AC-PAT-010.3، AC-PAT-002.2). */
export function PatientBadgeChips({ badges, guardianMissing = false }: { badges: Badges; guardianMissing?: boolean }) {
  const { t } = useTranslation('pat');
  const items: { key: string; color: 'default' | 'warning' | 'info' | 'error' }[] = [];
  if (badges.minor) {
    items.push({ key: 'minor', color: 'info' });
  }
  if (guardianMissing) {
    items.push({ key: 'guardian_missing', color: 'warning' });
  }
  if (badges.vip) {
    items.push({ key: 'vip', color: 'default' });
  }
  if (badges.incomplete) {
    items.push({ key: 'incomplete', color: 'warning' });
  }
  if (badges.deceased) {
    items.push({ key: 'deceased', color: 'error' });
  }
  if (items.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.5} component="span">
      {items.map((item) => (
        <Chip
          key={item.key}
          size="small"
          variant="outlined"
          color={item.color}
          label={t(`pat:badges.${item.key}`)}
          data-testid={`badge-${item.key}`}
        />
      ))}
    </Stack>
  );
}
